// Skyward PM C3 — login backdrop scenes (V0.2.1)
// Three canvas scenes drawn behind the sign-in card. Pure drawing code, no React.
// A = Drawing sheet · B = Island contours · C = Structure rising

export type BackdropVariant = 'drawing' | 'contours' | 'structure';

export interface Scene {
  /** Called on start and whenever the canvas size changes. */
  init(w: number, h: number): void;
  /** Draw one frame. t = seconds since start. */
  draw(ctx: CanvasRenderingContext2D, w: number, h: number, t: number): void;
  /** Time to draw when reduced motion is on (a finished, still frame). */
  stillTime: number;
  /** Max frames per second (contours are heavier). */
  fps: number;
}

const ORANGE = '#C55A11';
const ORANGE_HI = '#E8803A';
const narrowW = 560;

/* ---------------- A: Drawing sheet ---------------- */
type Seg =
  | { t: 'l'; x1: number; y1: number; x2: number; y2: number; k: 'm' | 't' | 'd'; len: number }
  | { t: 'c'; x: number; y: number; r: number; k: 'd'; len: number };
type Txt = { x: number; y: number; str: string; al: CanvasTextAlign; at: number };

export function drawingScene(): Scene {
  let segs: Seg[] = [];
  let texts: Txt[] = [];
  let total = 0;
  let seed = 13;
  let cycle = -1;
  let W = 0;
  let H = 0;
  const rnd = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };

  function build() {
    const s: Seg[] = [];
    const tx: Txt[] = [];
    const narrow = W < narrowW;
    const nb = 3 + Math.floor(rnd() * 3);
    const floors = 1 + Math.floor(rnd() * 2);
    const areaW = narrow ? W * 0.86 : W * 0.52;
    const left = narrow ? W * 0.07 : W * 0.42;
    const bw = areaW / nb;
    const fh = Math.min(H * 0.16, bw * 0.75);
    const g = narrow ? H * 0.36 : H * 0.72;
    const roofH = fh * (0.55 + rnd() * 0.35);
    const top = g - floors * fh;
    const ov = bw * 0.18;
    const L = (x1: number, y1: number, x2: number, y2: number, k: 'm' | 't' | 'd') =>
      s.push({ t: 'l', x1, y1, x2, y2, k, len: Math.hypot(x2 - x1, y2 - y1) });
    const T = (x: number, y: number, str: string, al: CanvasTextAlign = 'center') =>
      tx.push({ x, y, str, al, at: s.length });

    L(left - bw * 0.6, g, left + areaW + bw * 0.6, g, 'm');
    for (let i = 0; i <= nb; i++) L(left + i * bw, g, left + i * bw, top, 'm');
    for (let f = 1; f <= floors; f++) L(left, g - f * fh, left + areaW, g - f * fh, 'm');
    const rx = left + areaW / 2;
    L(left - ov, top + ov * 0.3, rx, top - roofH, 'm');
    L(rx, top - roofH, left + areaW + ov, top + ov * 0.3, 'm');
    for (let f = 0; f < floors; f++)
      for (let i = 0; i < nb; i++) {
        const x = left + i * bw + bw * 0.22, w = bw * 0.56;
        const y = g - f * fh - fh * 0.78, h = fh * (f === 0 ? 0.78 : 0.55);
        L(x, y, x + w, y, 't'); L(x + w, y, x + w, y + h, 't');
        L(x + w, y + h, x, y + h, 't'); L(x, y + h, x, y, 't');
        if (f === 0) L(x + w / 2, y, x + w / 2, y + h, 't');
      }
    // grid bubbles
    const by = top - roofH - 26, r = 9, letters = 'ABCDEFG';
    for (let i = 0; i <= nb; i++) {
      const x = left + i * bw;
      L(x, by + r, x, top - 4, 'd');
      s.push({ t: 'c', x, y: by, r, k: 'd', len: 2 * Math.PI * r });
      T(x, by + 3.5, letters[i]);
    }
    // dimension string (mm)
    const dy = g + 26;
    L(left, dy, left + areaW, dy, 'd');
    for (let i = 0; i <= nb; i++) {
      const x = left + i * bw;
      L(x - 4, dy + 4, x + 4, dy - 4, 'd');
      if (i < nb) T(x + bw / 2, dy - 6, String(Math.round((3600 + rnd() * 2400) / 300) * 300));
    }
    // level markers
    const lx = left + areaW + bw * 0.75;
    for (let f = 0; f <= floors; f++) {
      const y = g - f * fh;
      L(lx - 14, y, lx + 60, y, 'd'); L(lx - 7, y - 7, lx, y, 'd'); L(lx, y, lx + 7, y - 7, 'd');
      T(lx + 4, y - 10, 'FFL +' + (f * 3.6).toFixed(3), 'left');
    }
    segs = s; texts = tx; total = s.reduce((a, b) => a + b.len, 0);
  }

  function grid(ctx: CanvasRenderingContext2D, off: number) {
    ctx.lineWidth = 1;
    for (const [step, a] of [[20, 0.045], [100, 0.09]] as const) {
      ctx.strokeStyle = `rgba(234,214,194,${a})`;
      ctx.beginPath();
      for (let x = (off % step) - step; x < W; x += step) { ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, H); }
      for (let y = 0; y < H; y += step) { ctx.moveTo(0, y + 0.5); ctx.lineTo(W, y + 0.5); }
      ctx.stroke();
    }
  }

  return {
    stillTime: 9.5,
    fps: 60,
    init(w, h) { W = w; H = h; seed = (Math.max(cycle, 0)) * 7919 + 13; build(); },
    draw(ctx, w, h, t) {
      if (w !== W || h !== H) { W = w; H = h; build(); }
      const per = 14, c = Math.floor(t / per), lt = t - c * per;
      if (c !== cycle) { cycle = c; seed = c * 7919 + 13; build(); }
      const drift = Math.sin(t * 0.05) * 30;
      ctx.fillStyle = '#121110'; ctx.fillRect(0, 0, W, H);
      grid(ctx, drift);
      const p = Math.min(1, lt / 9);
      const fade = lt > 12.5 ? Math.max(0, 1 - (lt - 12.5) / 1.5) : 1;
      ctx.save(); ctx.globalAlpha = fade; ctx.translate(drift * 0.6, 0);
      let rem = p * total, done = 0;
      let pen: [number, number] | null = null;
      for (let i = 0; i < segs.length; i++) {
        const sg = segs[i];
        if (rem <= 0) break;
        const f = Math.min(1, rem / sg.len); rem -= sg.len; if (f >= 1) done = i + 1;
        ctx.strokeStyle = sg.k === 'm' ? ORANGE : sg.k === 't' ? 'rgba(234,214,194,.55)' : 'rgba(234,214,194,.38)';
        ctx.lineWidth = sg.k === 'm' ? 1.6 : 1;
        ctx.setLineDash(sg.t === 'l' && sg.k === 'd' && Math.abs(sg.x1 - sg.x2) < 1 ? [4, 4] : []);
        ctx.beginPath();
        if (sg.t === 'l') {
          const x = sg.x1 + (sg.x2 - sg.x1) * f, y = sg.y1 + (sg.y2 - sg.y1) * f;
          ctx.moveTo(sg.x1, sg.y1); ctx.lineTo(x, y); pen = [x, y];
        } else {
          const a = -Math.PI / 2 + f * Math.PI * 2;
          ctx.arc(sg.x, sg.y, sg.r, -Math.PI / 2, a); pen = [sg.x + sg.r * Math.cos(a), sg.y + sg.r * Math.sin(a)];
        }
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.font = '10px Arial'; ctx.fillStyle = 'rgba(234,214,194,.6)';
      for (const tx of texts) if (tx.at <= done) { ctx.textAlign = tx.al; ctx.fillText(tx.str, tx.x, tx.y); }
      if (pen && p < 1) {
        const gr = ctx.createRadialGradient(pen[0], pen[1], 0, pen[0], pen[1], 14);
        gr.addColorStop(0, 'rgba(232,128,58,.9)'); gr.addColorStop(1, 'rgba(232,128,58,0)');
        ctx.fillStyle = gr; ctx.beginPath(); ctx.arc(pen[0], pen[1], 14, 0, 7); ctx.fill();
      }
      ctx.restore();
      // title block
      const bw = 170, bh = 44, x = W - bw - 14, y = H - bh - 14;
      ctx.strokeStyle = 'rgba(234,214,194,.3)'; ctx.lineWidth = 1; ctx.strokeRect(x + 0.5, y + 0.5, bw, bh);
      ctx.beginPath(); ctx.moveTo(x, y + 22.5); ctx.lineTo(x + bw, y + 22.5);
      ctx.moveTo(x + 110.5, y + 22); ctx.lineTo(x + 110.5, y + bh); ctx.stroke();
      ctx.fillStyle = 'rgba(234,214,194,.6)'; ctx.font = '9.5px Arial'; ctx.textAlign = 'left';
      ctx.fillText('SWI-TAV · NORTH ELEVATION', x + 6, y + 15);
      ctx.fillText('A-2' + String(c % 10).padStart(2, '0'), x + 6, y + 37);
      ctx.fillText('REV ' + 'ABCDEF'[c % 6], x + 116, y + 37);
    },
  };
}

/* ---------------- B: Island contours ---------------- */
export interface ContourOptions {
  /** Corner readout, e.g. the site's coordinates. Default = Tavarua. */
  coords?: string;
}

export function contourScene(opts: ContourOptions = {}): Scene {
  const coords = opts.coords ?? '17°51′S  177°12′E';
  const cell = 9;
  let v = new Float32Array(0);
  function field(x: number, y: number, t: number, W: number, H: number) {
    const narrow = W < narrowW;
    const cx = narrow ? (0.5 * W) / H : (0.68 * W) / H, cy = narrow ? 0.28 : 0.5;
    const dx = (x - cx) / 0.34, dy = (y - cy) / 0.22, rot = 0.35;
    const u = dx * Math.cos(rot) - dy * Math.sin(rot), w = dx * Math.sin(rot) + dy * Math.cos(rot);
    let f = 1.15 * Math.exp(-(u * u + w * w));
    f += 0.16 * Math.sin(x * 5.1 + t * 0.11) * Math.cos(y * 6.3 - t * 0.08);
    f += 0.09 * Math.sin((x + y) * 9.7 + t * 0.17);
    f += 0.06 * Math.cos(x * 14.2 - y * 11.3 - t * 0.21);
    return f;
  }
  return {
    stillTime: 0,
    fps: 30,
    init() {},
    draw(ctx, W, H, t) {
      ctx.fillStyle = '#101314'; ctx.fillRect(0, 0, W, H);
      const cols = Math.ceil(W / cell) + 1, rows = Math.ceil(H / cell) + 1;
      if (v.length !== cols * rows) v = new Float32Array(cols * rows);
      for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) v[j * cols + i] = field((i * cell) / H, (j * cell) / H, t, W, H);
      for (let k = -6; k <= 13; k++) {
        const lv = k * 0.075, index = (k + 1000) % 5 === 0, land = lv > 0.45;
        ctx.strokeStyle = index
          ? land ? 'rgba(232,128,58,.75)' : 'rgba(197,90,17,.5)'
          : land ? 'rgba(234,214,194,.32)' : 'rgba(150,180,185,.16)';
        ctx.lineWidth = index ? 1.3 : 0.9;
        ctx.beginPath();
        const seg = (p: number[], q: number[]) => { ctx.moveTo(p[0], p[1]); ctx.lineTo(q[0], q[1]); };
        for (let j = 0; j < rows - 1; j++)
          for (let i = 0; i < cols - 1; i++) {
            const a = v[j * cols + i], b = v[j * cols + i + 1], c = v[(j + 1) * cols + i + 1], d = v[(j + 1) * cols + i];
            const code = (a > lv ? 8 : 0) | (b > lv ? 4 : 0) | (c > lv ? 2 : 0) | (d > lv ? 1 : 0);
            if (code === 0 || code === 15) continue;
            const x = i * cell, y = j * cell, it = (p: number, q: number) => (lv - p) / (q - p);
            const T = [x + cell * it(a, b), y], R = [x + cell, y + cell * it(b, c)];
            const B = [x + cell * it(d, c), y + cell], Lf = [x, y + cell * it(a, d)];
            switch (code) {
              case 1: case 14: seg(Lf, B); break;
              case 2: case 13: seg(B, R); break;
              case 3: case 12: seg(Lf, R); break;
              case 4: case 11: seg(T, R); break;
              case 5: seg(Lf, T); seg(B, R); break;
              case 6: case 9: seg(T, B); break;
              case 7: case 8: seg(Lf, T); break;
              case 10: seg(T, R); seg(Lf, B); break;
            }
          }
        ctx.stroke();
      }
      ctx.font = '10px ui-monospace, Menlo, Consolas, monospace';
      ctx.fillStyle = 'rgba(234,214,194,.55)'; ctx.textAlign = 'right';
      ctx.fillText(coords, W - 14, H - 30);
      ctx.fillText('CONTOUR 0.5 m · DATUM MSL', W - 14, H - 16);
    },
  };
}

/* ---------------- C: Structure rising ---------------- */
type El = { k: 'col' | 'bx' | 'by' | 'slab' | 'roof'; l: number; i: number; j: number; s: number };

export function structureScene(): Scene {
  const nx = 4, ny = 2, nl = 3, hz = 0.95;
  const els: El[] = [];
  let tt = 0.4;
  for (let l = 0; l < nl; l++) {
    for (let i = 0; i <= nx; i++) for (let j = 0; j <= ny; j++) { els.push({ k: 'col', l, i, j, s: tt }); tt += 0.07; }
    tt += 0.35;
    for (let j = 0; j <= ny; j++) for (let i = 0; i < nx; i++) { els.push({ k: 'bx', l, i, j, s: tt }); tt += 0.06; }
    for (let i = 0; i <= nx; i++) for (let j = 0; j < ny; j++) { els.push({ k: 'by', l, i, j, s: tt }); tt += 0.06; }
    tt += 0.3; els.push({ k: 'slab', l, i: 0, j: 0, s: tt }); tt += 0.8;
  }
  els.push({ k: 'roof', l: nl, i: 0, j: 0, s: tt });
  const end = tt + 1.2;
  let s = 1, ox = 0, oy = 0;
  const P = (x: number, y: number, z: number): [number, number] => [ox + (x - y) * 0.866 * s, oy + (x + y) * 0.5 * s - z * s];
  const col = (age: number) => {
    if (age < 1.2) return ORANGE_HI;
    const k = Math.min(1, (age - 1.2) / 1.5);
    return `rgba(${Math.round(232 - 62 * k)},${Math.round(128 + 32 * k)},${Math.round(58 + 92 * k)},${1 - 0.25 * k})`;
  };
  return {
    stillTime: end,
    fps: 60,
    init() {},
    draw(ctx, W, H, t) {
      const n = W < narrowW;
      s = n ? Math.min((W * 0.8) / 5.2, (H * 0.42) / 6.6) : Math.min((W * 0.48) / 5.2, (H * 0.8) / 6.6);
      ox = (n ? W * 0.5 : W * 0.7) - 0.866 * s;
      oy = (n ? H * 0.05 : H * 0.1) + 3.65 * s;
      const per = end + 3.5, lt = t % per;
      const fade = lt > end + 2 ? Math.max(0, 1 - (lt - end - 2) / 1.5) : 1;
      ctx.fillStyle = '#121110'; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = 'rgba(234,214,194,.07)'; ctx.lineWidth = 1; ctx.beginPath();
      for (let i = -4; i <= nx + 4; i++) { const a = P(i, -3, 0), b = P(i, ny + 3, 0); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); }
      for (let j = -3; j <= ny + 3; j++) { const a = P(-4, j, 0), b = P(nx + 4, j, 0); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); }
      ctx.stroke();
      ctx.save(); ctx.globalAlpha = fade;
      const line = (a: number[], b: number[], c: string) => { ctx.strokeStyle = c; ctx.beginPath(); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); ctx.stroke(); };
      let built = 0;
      for (const e of els) {
        const age = lt - e.s;
        if (age < 0) continue;
        const p = Math.min(1, age / 0.5), ep = 1 - Math.pow(1 - p, 3);
        if (p >= 1) built++;
        const z0 = e.l * hz, z1 = z0 + hz;
        ctx.lineWidth = e.k === 'col' ? 2 : 1.4;
        if (e.k === 'col') line(P(e.i, e.j, z0), P(e.i, e.j, z0 + hz * ep), col(age));
        else if (e.k === 'bx') line(P(e.i, e.j, z1), P(e.i + ep, e.j, z1), col(age));
        else if (e.k === 'by') line(P(e.i, e.j, z1), P(e.i, e.j + ep, z1), col(age));
        else if (e.k === 'slab') {
          const q = [P(0, 0, z1), P(nx, 0, z1), P(nx, ny, z1), P(0, ny, z1)];
          ctx.fillStyle = `rgba(234,214,194,${0.09 * ep})`; ctx.beginPath();
          q.forEach((pt, i) => (i ? ctx.lineTo(pt[0], pt[1]) : ctx.moveTo(pt[0], pt[1])));
          ctx.closePath(); ctx.fill();
          ctx.strokeStyle = `rgba(234,214,194,${0.12 * ep})`; ctx.lineWidth = 1; ctx.beginPath();
          for (let r = 0.25; r < nx; r += 0.25) { const a = P(r, 0, z1), b = P(r, ny * ep, z1); ctx.moveTo(a[0], a[1]); ctx.lineTo(b[0], b[1]); }
          ctx.stroke();
        } else {
          const zr = e.l * hz, rh = 0.7 * ep, a = P(0, ny / 2, zr + rh), b = P(nx, ny / 2, zr + rh), c = col(age);
          ctx.lineWidth = 1.6;
          line(P(0, 0, zr), a, c); line(P(0, ny, zr), a, c); line(P(nx, 0, zr), b, c); line(P(nx, ny, zr), b, c); line(a, b, c);
        }
      }
      ctx.restore();
      const pct = Math.round((100 * built) / els.length);
      const lvl = Math.min(nl, Math.floor(Math.max(0, lt - 0.4) / (end / nl)) + 1);
      ctx.font = '10px ui-monospace, Menlo, Consolas, monospace'; ctx.textAlign = 'right'; ctx.fillStyle = 'rgba(234,214,194,.55)';
      ctx.fillText(`${pct >= 100 ? 'TOPPED OUT' : 'LEVEL ' + lvl} · ${String(pct).padStart(3, ' ')}% COMPLETE`, W - 14, H - 30);
      const bw = 150, x = W - 14 - bw, y = H - 20;
      ctx.fillStyle = 'rgba(234,214,194,.12)'; ctx.fillRect(x, y, bw, 3);
      ctx.fillStyle = ORANGE; ctx.fillRect(x, y, (bw * pct) / 100, 3);
    },
  };
}

export const VARIANTS: BackdropVariant[] = ['drawing', 'contours', 'structure'];

export function makeScene(v: BackdropVariant, opts: ContourOptions = {}): Scene {
  return v === 'drawing' ? drawingScene() : v === 'contours' ? contourScene(opts) : structureScene();
}
